import axios from 'axios';
import {
  CreateChatAssetRequest,
  CreateChatItemRequest,
  CreateChatLibraryEntryRequest,
  CreateChatRequest,
  ChatAsset,
  ChatLibraryEntry,
  ChatApiDetail,
  ChatDetail,
  ChatEligibleUser,
  ChatReactionEmoji,
  ChatReactionResponse,
  ChatSummary,
  ChatTimelineItem,
  ChatUnreadCountResponse,
  SetChatReactionRequest,
} from './models/Chat';
import { applyClientChannelHeader } from './utils/clientChannel';
import { applyAllowTestOrgsHeader } from './utils/experimentalFeatures';
import type { ChatInbox } from './utils/chat';

const baseURL = process.env.REACT_APP_API_BASE_URL;

function mapChatApiDetail(data: ChatApiDetail): ChatDetail {
  return {
    summary: data.chat,
    items: data.items.map((item) => ({
      ...item,
      assetIds: item.assetIds?.length ? item.assetIds : item.assetId ? [item.assetId] : [],
      reactions: item.reactions ?? [],
      reactionEmoji: item.reactionEmoji ?? null,
    })),
    assets: data.assets,
  };
}

class ChatApiClient {
  public axiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      baseURL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    this.axiosInstance.interceptors.request.use(
      (config) => {
        const accessToken = localStorage.getItem('access_token');
        const assumedUserId = localStorage.getItem('assumed_user_id');

        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`;
        }
        if (assumedUserId) {
          config.headers['X-UserId'] = assumedUserId;
        }
        const assumedOrganizationId = localStorage.getItem('assumed_organization_id');
        if (assumedOrganizationId) {
          config.headers['X-OrganizationId'] = assumedOrganizationId;
        }

        applyClientChannelHeader(config.headers);
        applyAllowTestOrgsHeader(config.headers);

        return config;
      },
      (error) => Promise.reject(error)
    );

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error.response ? error.response.status : null;
        // Only clear session on 401 (invalid/expired token). 403 is authorization, not auth failure.
        if (status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('phone_number');
        }
        return Promise.reject(error);
      }
    );
  }

  public async listChats(inbox: ChatInbox): Promise<ChatSummary[]> {
    const response = await this.axiosInstance.get<ChatSummary[]>(`/${inbox}/chats`);
    return response.data;
  }

  public async getUnreadCount(inbox: ChatInbox): Promise<ChatUnreadCountResponse> {
    const response = await this.axiosInstance.get<ChatUnreadCountResponse>(
      `/${inbox}/chats/unread-count`
    );
    return response.data;
  }

  public async listEligibleUsers(): Promise<ChatEligibleUser[]> {
    const response = await this.axiosInstance.get<ChatEligibleUser[]>('/chats/eligible-users');
    return response.data;
  }

  public async createChat(request: CreateChatRequest): Promise<ChatSummary> {
    const response = await this.axiosInstance.post<ChatApiDetail>('/chats', request);
    return response.data.chat;
  }

  public async getChat(chatId: string): Promise<ChatDetail> {
    const response = await this.axiosInstance.get<ChatApiDetail>(`/chats/${chatId}`);
    return mapChatApiDetail(response.data);
  }

  public async markChatRead(chatId: string): Promise<void> {
    await this.axiosInstance.post(`/chats/${chatId}/read`);
  }

  public async addTimelineItem(
    chatId: string,
    request: CreateChatItemRequest
  ): Promise<ChatTimelineItem> {
    const response = await this.axiosInstance.post<ChatTimelineItem>(
      `/chats/${chatId}/items`,
      request
    );
    return response.data;
  }

  public async addAsset(chatId: string, request: CreateChatAssetRequest): Promise<ChatAsset> {
    const response = await this.axiosInstance.post<ChatAsset>(
      `/chats/${chatId}/assets`,
      request
    );
    return response.data;
  }

  public async listLibrary(chatId: string): Promise<ChatLibraryEntry[]> {
    const response = await this.axiosInstance.get<ChatLibraryEntry[]>(
      `/chats/${chatId}/library`
    );
    return response.data;
  }

  public async saveToLibrary(
    chatId: string,
    request: CreateChatLibraryEntryRequest
  ): Promise<ChatLibraryEntry> {
    const response = await this.axiosInstance.post<ChatLibraryEntry>(
      `/chats/${chatId}/library`,
      request
    );
    return response.data;
  }

  public async removeFromLibrary(chatId: string, libraryEntryId: string): Promise<void> {
    await this.axiosInstance.delete(`/chats/${chatId}/library/${libraryEntryId}`);
  }

  public async setReaction(
    chatId: string,
    itemId: string,
    emoji: ChatReactionEmoji
  ): Promise<ChatReactionResponse> {
    const response = await this.axiosInstance.post<ChatReactionResponse>(
      `/chats/${chatId}/items/${itemId}/reaction`,
      { emoji } satisfies SetChatReactionRequest
    );
    return response.data;
  }

  public async clearReaction(chatId: string, itemId: string): Promise<void> {
    await this.axiosInstance.delete(`/chats/${chatId}/items/${itemId}/reaction`);
  }
}

export default ChatApiClient;

export const chatApiClient = new ChatApiClient();
